import { Message } from '../types';
import { PROMPTS } from '../constants/prompts';
import ApiKeyService from '../services/ApiKeyService';

const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

// API 키를 동적으로 가져오는 함수
const getOpenAIApiKey = (): string => {
  const apiKeyService = ApiKeyService.getInstance();
  const apiKey = apiKeyService.openAiApiKey;
  
  if (!apiKey) {
    throw new Error('OpenAI API 키가 로드되지 않았습니다. 앱을 새로고침해주세요.');
  }
  
  return apiKey;
};

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export const callOpenAI = async (
  messages: ChatMessage[]
): Promise<string> => {
  try {
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getOpenAIApiKey()}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages,
        temperature: 0.9,
        max_tokens: 1000,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content || '';
  } catch (error) {
    console.error('OpenAI API call failed:', error);
    throw error;
  }
};

export const callOpenAIStream = async (
  messages: ChatMessage[],
  onChunk: (chunk: string) => void
): Promise<void> => {
  try {
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getOpenAIApiKey()}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages,
        temperature: 0.9,
        max_tokens: 1000,
        stream: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') return;

          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices[0]?.delta?.content;
            if (content) {
              onChunk(content);
            }
          } catch (e) {
            // Ignore parsing errors for incomplete chunks
          }
        }
      }
    }
  } catch (error) {
    console.error('OpenAI streaming API call failed:', error);
    throw error;
  }
};

export const generateSoulResponse = async (
  messages: Message[],
  language: string = 'ko'
): Promise<string> => {
  const systemPrompt = PROMPTS[language as keyof typeof PROMPTS]?.soulChatSystemPrompt || PROMPTS.ko.soulChatSystemPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    ...messages.map(msg => ({
      role: msg.role as 'user' | 'assistant',
      content: msg.content
    }))
  ];

  return await callOpenAI(chatMessages);
};

export const generateSoulResponseStream = async (
  messages: Message[],
  language: string = 'ko',
  onChunk: (chunk: string) => void,
  isRoutine: boolean = false
): Promise<void> => {
  // 루틴 진입 시와 쏘울 진입 시 다른 프롬프트 사용
  const systemPrompt = isRoutine 
    ? PROMPTS[language as keyof typeof PROMPTS]?.chatSystemPrompt || PROMPTS.ko.chatSystemPrompt
    : PROMPTS[language as keyof typeof PROMPTS]?.soulChatSystemPrompt || PROMPTS.ko.soulChatSystemPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    ...messages.map(msg => ({
      role: msg.role as 'user' | 'assistant',
      content: msg.content
    }))
  ];

  await callOpenAIStream(chatMessages, onChunk);
};

export const generateRoutineCompletionResponse = async (
  emotion: string,
  activity: string,
  language: string = 'ko'
): Promise<string> => {
  const systemPrompt = PROMPTS[language as keyof typeof PROMPTS]?.routineCompletionSoulPrompt || PROMPTS.ko.routineCompletionSoulPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { 
      role: 'user', 
      content: `emotion: ${emotion}\nactivity: ${activity}` 
    }
  ];

  return await callOpenAI(chatMessages);
};

export const generateRoutineCompletionResponseStream = async (
  emotion: string,
  activity: string,
  language: string = 'ko',
  onChunk: (chunk: string) => void
): Promise<void> => {
  const systemPrompt = PROMPTS[language as keyof typeof PROMPTS]?.routineCompletionSoulPrompt || PROMPTS.ko.routineCompletionSoulPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { 
      role: 'user', 
      content: `emotion: ${emotion}\nactivity: ${activity}` 
    }
  ];

  await callOpenAIStream(chatMessages, onChunk);
};

export const generateWelcomeMessage = async (
  routineTitle: string,
  routineDescription: string,
  language: string = 'ko'
): Promise<string> => {
  const systemPrompt = PROMPTS[language as keyof typeof PROMPTS]?.welcomeSystemPrompt || PROMPTS.ko.welcomeSystemPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { 
      role: 'user', 
      content: `routine title: ${routineTitle}\ndescription: ${routineDescription}` 
    }
  ];

  return await callOpenAI(chatMessages);
};

export const generateWelcomeMessageStream = async (
  routineTitle: string,
  routineDescription: string,
  language: string = 'ko',
  onChunk: (chunk: string) => void
): Promise<void> => {
  const systemPrompt = PROMPTS[language as keyof typeof PROMPTS]?.welcomeSystemPrompt || PROMPTS.ko.welcomeSystemPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { 
      role: 'user', 
      content: `routine title: ${routineTitle}\ndescription: ${routineDescription}` 
    }
  ];

  await callOpenAIStream(chatMessages, onChunk);
};

export const generateRoutineWelcomeMessageStream = async (
  routineTitle: string,
  routineDescription: string,
  language: string = 'ko',
  onChunk: (chunk: string) => void
): Promise<void> => {
  const systemPrompt = PROMPTS[language as keyof typeof PROMPTS]?.welcomeSystemPrompt || PROMPTS.ko.welcomeSystemPrompt;
  
  const chatMessages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { 
      role: 'user', 
      content: `routine title: ${routineTitle}\ndescription: ${routineDescription}` 
    }
  ];

  await callOpenAIStream(chatMessages, onChunk);
}; 